// ============================================================
// MongoDBAdvancedQueries.jsx
//
// A branded guide to Full-Text Search, Geospatial Queries,
// and Regex Pattern Matching in MongoDB.
//
// Brand palette:
//   Background  → #001E2B  (MongoDB Deep Navy)
//   Primary     → #C084FC  (Electric Purple / Advanced Intelligence)
//   Accent      → #00ED64  (MongoDB Green)
//   Text        → #FFFFFF  (White)
//   Code        → #010409  (GitHub Dark)
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    IconButton,
    Avatar,
    Button,
    Tooltip
} from "@mui/material";

/* MUI Icons */
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import SearchIcon from "@mui/icons-material/Search";
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import MapIcon from "@mui/icons-material/Map";
import PsychologyIcon from "@mui/icons-material/Psychology";
import FilterListIcon from "@mui/icons-material/FilterList";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#001E2B",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(192, 132, 252, 0.2)", // Purple Border
    purple: "#C084FC",
    green: "#00ED64",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "#010409",
};

/* ── Sub-Components ──────────────────────────────────────── */

function CodeTerminal({ code, label, color = BRAND.purple }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.02)" }}>
                <Typography variant="caption" sx={{ color: color, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ p: 2.5, overflowX: "auto" }}>
                <Typography component="pre" sx={{ fontFamily: "'Fira Code', monospace", fontSize: 13, color: "#E9D5FF", lineHeight: 1.5 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

/* ── Main Component ──────────────────────────────────────── */

export default function MongoDBAdvancedQueries() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon style={{ color: BRAND.purple }} />}
                        label="Query Optimization · Module 12"
                        sx={{ bgcolor: "rgba(192,132,252,0.1)", color: BRAND.purple, border: `1px solid ${BRAND.border}`, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Advanced <span style={{ color: BRAND.purple }}>Queries</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                        Master the intelligence of MongoDB. Beyond simple equality, learn to leverage
                        <strong> Full-Text Search</strong> for content discovery and <strong>Geo-Spatial </strong>
                        logic for location-aware features like Uber or Yelp.
                    </Typography>
                </Box>

                {/* 2. FULL-TEXT SEARCH */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <PsychologyIcon sx={{ color: BRAND.purple, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Full-Text Search ($text)</Typography>
                    </Box>
                    <Typography sx={{ color: BRAND.muted, mb: 4, lineHeight: 1.8 }}>
                        Standard regex is slow on large datasets. MongoDB's <strong>Text Index</strong> allows you to search
                        through strings with "Stemming" (searching for 'run' finds 'running') and "Stop Word" removal.
                    </Typography>



                    <Grid container spacing={4}>
                        <Grid item xs={12} md={7}>
                            <CodeTerminal
                                label="mongosh: Text Search"
                                code={`// 1. Create the index\ndb.stores.createIndex({ description: "text" });\n\n// 2. Search for keywords\ndb.stores.find({ $text: { $search: "coffee cake" } });\n\n// 3. Search with relevance score\ndb.stores.find(\n  { $text: { $search: "espresso" } },\n  { score: { $meta: "textScore" } }\n).sort({ score: { $meta: "textScore" } });`}
                            />
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Stack spacing={2}>
                                <Paper sx={{ p: 3, bgcolor: BRAND.surface, borderRadius: 3, border: `1px solid ${BRAND.border}` }}>
                                    <Typography variant="subtitle2" sx={{ color: BRAND.purple }}>Language Support</Typography>
                                    <Typography variant="body2" sx={{ color: BRAND.muted }}>Supports over 15 languages out of the box for accurate stemming.</Typography>
                                </Paper>
                                <Paper sx={{ p: 3, bgcolor: BRAND.surface, borderRadius: 3, border: `1px solid ${BRAND.border}` }}>
                                    <Typography variant="subtitle2" sx={{ color: BRAND.purple }}>Negation Search</Typography>
                                    <Typography variant="body2" sx={{ color: BRAND.muted }}>Use a minus sign <code>-decaf</code> to exclude words from results.</Typography>
                                </Paper>
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 10 }} />

                {/* 3. GEOSPATIAL QUERIES */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <MyLocationIcon sx={{ color: BRAND.purple, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Geospatial Intelligence</Typography>
                    </Box>
                    <Typography sx={{ color: BRAND.muted, mb: 4, lineHeight: 1.8 }}>
                        Storing coordinates as <strong>GeoJSON</strong> objects allows you to calculate distances
                        on a sphere. Use <code>$nearSphere</code> to find documents closest to a point.
                    </Typography>



                    <Grid container spacing={4}>
                        <Grid item xs={12} md={5}>
                            <Stack spacing={2}>
                                <Paper sx={{ p: 3, bgcolor: BRAND.surface, borderRadius: 3, border: `1px solid ${BRAND.border}` }}>
                                    <Typography variant="subtitle2" sx={{ color: BRAND.purple }}>2dsphere Index</Typography>
                                    <Typography variant="body2" sx={{ color: BRAND.muted }}>Required to calculate geometries on an earth-like sphere.</Typography>
                                </Paper>
                                <Paper sx={{ p: 3, bgcolor: BRAND.surface, borderRadius: 3, border: `1px solid ${BRAND.border}` }}>
                                    <Typography variant="subtitle2" sx={{ color: BRAND.purple }}>$geoWithin</Typography>
                                    <Typography variant="body2" sx={{ color: BRAND.muted }}>Finds documents inside a specific Polygon (e.g., a city boundary).</Typography>
                                </Paper>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <CodeTerminal
                                label="mongosh: Location Query"
                                code={`// Find stores within 5000 meters of a user\ndb.places.find({\n  location: {\n    $nearSphere: {\n      $geometry: {\n        type: "Point",\n        coordinates: [ -73.96, 40.78 ]\n      },\n      $maxDistance: 5000\n    }\n  }\n});`}
                            />
                        </Grid>
                    </Grid>
                </Box>

                {/* 4. REGEX & LOGIC */}
                <Box sx={{ mb: 10, p: 4, borderRadius: 6, bgcolor: "rgba(192, 132, 252, 0.03)", border: `1px solid ${BRAND.border}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                        <FilterListIcon sx={{ color: BRAND.purple }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Pattern Matching ($regex)</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: BRAND.muted, mb: 4 }}>
                        When you don't need a full text index, use <strong>Regular Expressions</strong> for flexible
                        string matching (like finding all users whose email ends in <code>.edu</code>).
                    </Typography>

                    <CodeTerminal
                        label="Regex Query"
                        code={`// Case-insensitive search for names starting with 'a'\ndb.users.find({ name: { $regex: /^a/i } });`}
                    />
                </Box>

                {/* FOOTER CTA */}
                <Box sx={{
                    p: 8, borderRadius: 8, textAlign: "center",
                    background: `linear-gradient(45deg, ${BRAND.bg}, #2E1065)`,
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: `0 20px 40px rgba(0,0,0,0.5)`
                }}>
                    <TravelExploreIcon style={{ fontSize: 40, color: BRAND.purple, marginBottom: 16 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Precision Search Ready?</Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>
                        Now that you can find anything, anywhere, let's learn how to protect that data with
                        Advanced Transactions.
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button variant="contained" sx={{ bgcolor: BRAND.purple, color: BRAND.bg, fontWeight: 900 }}>Next: Transactions</Button>
                        <Button variant="outlined" sx={{ color: BRAND.white, borderColor: BRAND.muted }}>GeoJSON Specs</Button>
                    </Stack>
                </Box>

            </Container>
        </Box>
    );
}
